import type { Skill } from "./types";
import type { CopilotTask, CopilotTaskStatus } from "../types";

const VALID_STATUSES: CopilotTaskStatus[] = [
  "pending",
  "in-progress",
  "done",
  "failed",
  "skipped",
];

/**
 * El sistema mantiene un task list visible al usuario. Estas skills lo
 * controlan. NO son skills "API" — solo afectan estado UI vía evento global.
 *
 * Eventos en window:
 *   - "orkestria:tasklist:set"  → detail: { title?, tasks }
 *   - "orkestria:tasklist:mark" → detail: { id, status, note? }
 *   - "orkestria:tasklist:clear"
 */

export const setTaskListSkill: Skill = {
  id: "setTaskList",
  description:
    "Declara la lista de pasos que vas a ejecutar para cumplir la solicitud del usuario. Úsala SIEMPRE al inicio de tareas multi-paso (más de 2 acciones). Lista cada acción concreta como una tarea separada con un id corto. Después usa markTask para marcar cada una como done/failed conforme avanzas. El usuario verá la lista visualmente arriba de su input.",
  schema: {
    type: "object",
    properties: {
      title: {
        type: "string",
        description: "Resumen breve (ej. 'Limpiar duplicados y crear menú')",
      },
      tasks: {
        type: "array",
        description:
          "Array de pasos en orden. Cada uno: { id (string corto único), label (descripción humana corta) }",
        items: {
          type: "object",
          properties: {
            id: { type: "string" },
            label: { type: "string" },
          },
          required: ["id", "label"],
        },
      },
    },
    required: ["tasks"],
  },
  async run(args) {
    const title = args.title ? String(args.title) : undefined;
    const rawTasks = Array.isArray(args.tasks) ? args.tasks : [];
    const newTasks: CopilotTask[] = rawTasks
      .map((t: any) => {
        const id = String(t?.id ?? "").trim();
        const label = String(t?.label ?? "").trim();
        if (!id || !label) return null;
        return { id, label, status: "pending" as CopilotTaskStatus };
      })
      .filter((t): t is CopilotTask => t !== null);

    if (newTasks.length === 0) {
      return { ok: false, error: "Se requiere al menos un task válido." };
    }

    if (typeof window !== "undefined") {
      // Lee el task list actual del state global expuesto en window
      const currentTaskList = (window as any).__orkestriaTaskList as
        | { tasks: CopilotTask[] }
        | undefined;

      // Si ya hay un task list con progreso (al menos 1 done), MERGE en vez de reset
      if (
        currentTaskList?.tasks &&
        currentTaskList.tasks.some((t) => t.status === "done")
      ) {
        const mergedTasks: CopilotTask[] = [];
        const seenIds = new Set<string>();
        // Preserva tasks existentes (con su estado)
        for (const t of currentTaskList.tasks) {
          mergedTasks.push(t);
          seenIds.add(t.id);
        }
        // Añade nuevos tasks (solo los que no existían)
        let addedCount = 0;
        for (const t of newTasks) {
          if (!seenIds.has(t.id)) {
            mergedTasks.push(t);
            addedCount++;
          }
        }
        window.dispatchEvent(
          new CustomEvent("orkestria:tasklist:set", {
            detail: { title, tasks: mergedTasks },
          }),
        );
        return {
          ok: true,
          result: { count: mergedTasks.length, added: addedCount },
          humanSummary:
            addedCount > 0
              ? `Plan actualizado: ${addedCount} task(s) nuevo(s) agregado(s) (merge con plan en curso).`
              : "El plan ya tenía esos tasks. Continuando con los pendientes.",
        };
      }

      // Sin task list previo o sin progreso: reemplazar
      window.dispatchEvent(
        new CustomEvent("orkestria:tasklist:set", {
          detail: { title, tasks: newTasks },
        }),
      );
    }

    return {
      ok: true,
      result: { count: newTasks.length },
      humanSummary: `Plan creado: ${newTasks.length} pasos.`,
    };
  },
};

export const markTaskSkill: Skill = {
  id: "markTask",
  description:
    "Actualiza el estado de un task del task list activo. Usa esto INMEDIATAMENTE después de completar (o fallar) cada acción declarada en setTaskList. Estados: 'in-progress' (al empezar), 'done' (completada), 'failed' (no se pudo), 'skipped' (omitida). El usuario ve el cambio en tiempo real.",
  schema: {
    type: "object",
    properties: {
      id: {
        type: "string",
        description: "ID del task (mismo que diste en setTaskList)",
      },
      status: {
        type: "string",
        enum: [...VALID_STATUSES],
      },
      note: {
        type: "string",
        description: "Nota corta opcional (ej. resultado o motivo de fallo)",
      },
    },
    required: ["id", "status"],
  },
  async run(args) {
    const id = String(args.id ?? "").trim();
    const status = String(args.status ?? "");
    if (!id || !VALID_STATUSES.includes(status as CopilotTaskStatus)) {
      return { ok: false, error: "id o status inválidos." };
    }
    const note = args.note ? String(args.note) : undefined;

    // ── Validar que el id existe en el task list actual ────────────────────
    // Bug previo: si el LLM pasaba un id inexistente, el dispatch no hacía
    // nada (map no encontraba match), pero la skill retornaba ok:true. El
    // LLM re-leía el taskList, veía la task aún pending y reintentaba → loop
    // infinito. Ahora validamos antes y devolvemos error con la lista de ids
    // válidos para que el LLM corrija.
    if (typeof window !== "undefined") {
      const currentTaskList = (window as any).__orkestriaTaskList as
        | { tasks: CopilotTask[] }
        | undefined;
      const tasks = currentTaskList?.tasks ?? [];
      const target = tasks.find((t) => t.id === id);
      if (!target) {
        const validIds = tasks.map((t) => t.id);
        return {
          ok: false,
          error:
            tasks.length === 0
              ? "No hay task list activo. Llama primero a setTaskList."
              : `No existe ningún task con id "${id}". Ids válidos: ${validIds.join(", ")}.`,
        };
      }
      if (target.status === status) {
        // Idempotente: ya está en ese estado, no re-disparar.
        return {
          ok: true,
          result: { id, status, alreadyInState: true },
          humanSummary: `Task ${id} ya estaba en ${status}.`,
        };
      }
      window.dispatchEvent(
        new CustomEvent("orkestria:tasklist:mark", {
          detail: { id, status, note },
        }),
      );
    }

    return {
      ok: true,
      result: { id, status },
      humanSummary: `Task ${id} → ${status}`,
    };
  },
};
